import React from "react";
import { useLocation } from "react-router-dom";
import Sidebar from "../Sidebar";

/**
 * Chặn lỗi render để một màn hỏng không làm trắng cả CMS.
 *
 * VÌ SAO CẦN: trước 08/10/2026 trong `src/` không có `componentDidCatch` nào. React 18 mà
 * render throw thì nó THÁO TOÀN BỘ cây — người dùng chỉ thấy trang trắng, không đọc được lỗi,
 * không bấm được menu để đi màn khác. Sự cố thật: `DataTable` gọi `rowKey` dạng chuỗi như hàm
 * ("l is not a function") làm vỡ cả 4 màn kèm toàn bộ CMS.
 *
 * Dùng ở đâu: `App.js` bọc quanh `<Routes>`. Bản có `withSidebar` vẫn vẽ lại `Sidebar` nên
 * khi một màn admin hỏng, menu trái còn dùng được để bấm sang màn khác.
 *
 * KHÔNG dùng `key` để reset: `key` đổi là remount CẢ cây con ngay cả lúc không có lỗi (mất
 * state mọi màn mỗi lần đổi URL). Ở đây dùng `resetKey` + `componentDidUpdate` nên chỉ xoá
 * trạng thái lỗi khi đang lỗi và người dùng đã điều hướng sang chỗ khác.
 *
 * Lỗi KHÔNG bắt được (giới hạn của React, không phải thiếu sót ở đây): lỗi trong event
 * handler, `setTimeout`, promise `.then` — những chỗ đó không nằm trong lúc render.
 */
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { error: null, stack: null };
    }

    static getDerivedStateFromError(error) {
        return { error };
    }

    componentDidCatch(error, info) {
        // Giữ nguyên console.error: stack minify (vd "DataTable.js:139") là thứ duy nhất lần
        // được ra chỗ hỏng trên bản production.
        console.error("[ErrorBoundary] màn hình gặp lỗi render:", error, info);
        this.setState({ stack: info && info.componentStack ? info.componentStack : null });
    }

    componentDidUpdate(prevProps) {
        if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
            this.setState({ error: null, stack: null });
        }
    }

    retry = () => this.setState({ error: null, stack: null });

    reload = () => window.location.reload();

    renderPanel() {
        const { error, stack } = this.state;
        const message = error && error.message ? error.message : String(error);
        return (
            <div className="card">
                <div className="card-body">
                    <h5 className="card-title text-danger mb-2">
                        <i className="fas fa-exclamation-triangle mr-2"></i>
                        Màn hình này gặp lỗi
                    </h5>
                    <p className="text-muted mb-3">
                        Phần còn lại của hệ thống vẫn hoạt động — bạn có thể chọn mục khác ở menu bên trái.
                        Nếu lỗi lặp lại, hãy gửi nội dung trong “Chi tiết kỹ thuật” cho bộ phận kỹ thuật.
                    </p>
                    <div className="mb-3">
                        <button type="button" className="btn btn-primary btn-sm mr-2" onClick={this.retry}>
                            <i className="fas fa-redo mr-1"></i> Thử lại
                        </button>
                        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={this.reload}>
                            <i className="fas fa-sync-alt mr-1"></i> Tải lại trang
                        </button>
                    </div>
                    <details>
                        <summary className="text-muted" style={{ cursor: "pointer" }}>Chi tiết kỹ thuật</summary>
                        <pre
                            className="mt-2 mb-0 p-2 bg-light border rounded"
                            style={{ whiteSpace: "pre-wrap", fontSize: 12, maxHeight: 260, overflow: "auto" }}
                        >
                            {message}
                            {stack ? "\n" + stack : ""}
                        </pre>
                    </details>
                </div>
            </div>
        );
    }

    render() {
        if (!this.state.error) {
            return this.props.children;
        }
        if (this.props.withSidebar) {
            return (
                <div className="container">
                    <div className="row">
                        <Sidebar />
                        <div className="mb-4 col-md-10 tab-content">{this.renderPanel()}</div>
                    </div>
                </div>
            );
        }
        return <div className="container my-4">{this.renderPanel()}</div>;
    }
}

/**
 * Bản dùng trong router: tự lấy `pathname` làm `resetKey` nên người dùng bấm sang màn khác là
 * hết thông báo lỗi, không phải F5.
 */
export function RouteErrorBoundary({ children, withSidebar }) {
    const location = useLocation();
    return (
        <ErrorBoundary resetKey={location.pathname} withSidebar={withSidebar}>
            {children}
        </ErrorBoundary>
    );
}

export default ErrorBoundary;
